import type * as React from 'react'
import { useCallback } from 'react'
import { AUTO_SAVE_DEBOUNCE_MS, useAutoSave } from '@/hooks/use-auto-save'
import { formatBigONotation } from '@/lib/format-complexity'
import { cn } from '@/lib/utils'
import { AutoSaveStatusIcon, autoSaveBorderClass } from './auto-save-status'
import { Textarea } from './textarea'

interface AutoSaveTextareaProps
  extends Omit<React.ComponentProps<'textarea'>, 'onChange' | 'value'> {
  /** Current value of the textarea */
  value: string
  /** Called when the textarea value changes */
  onChange: (value: string) => void
  /** Called to save the value - should return a promise */
  onSave: (value: string) => Promise<void>
  /** Called after save completes with the (possibly transformed) value */
  onSaveComplete?: (transformedValue: string) => void
  /** Debounce delay in milliseconds before triggering save */
  debounceMs?: number
  /** Whether to format Big O notation with backticks on save */
  formatOnSave?: boolean
  /**
   * Send a scheduled-but-unsent save on unmount instead of dropping it. Opt in
   * where the field can be unmounted while the edit is still meant to survive
   * -- a collapsible panel, a route change.
   */
  flushOnUnmount?: boolean
}

/**
 * A textarea component that auto-saves with debouncing and shows save status.
 * Displays a small icon in the bottom-right corner:
 * - Gray save icon when typing (unsaved changes)
 * - Blue spinning loader when saving
 * - Green check when saved
 */
function AutoSaveTextarea({
  value,
  onChange,
  onSave,
  onSaveComplete,
  debounceMs = AUTO_SAVE_DEBOUNCE_MS.fast,
  formatOnSave = false,
  flushOnUnmount = false,
  className,
  ...props
}: AutoSaveTextareaProps) {
  const { state, onChange: scheduleSave } = useAutoSave({
    value,
    onSave,
    debounceMs,
    skipUnchanged: true,
    transform: formatOnSave ? formatBigONotation : undefined,
    onSaved: (savedValue, requestedValue) => {
      // Notify the parent only when formatting actually rewrote the text.
      if (savedValue !== requestedValue) {
        onSaveComplete?.(savedValue)
      }
    },
    flushOnUnmount,
    logLabel: 'AutoSaveTextarea',
  })

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value)
      scheduleSave(e.target.value)
    },
    [onChange, scheduleSave],
  )

  return (
    <div className="relative">
      <Textarea
        value={value}
        onChange={handleChange}
        className={cn(
          'pr-8 transition-colors duration-200',
          autoSaveBorderClass(state),
          className,
        )}
        {...props}
      />
      <div className="pointer-events-none absolute right-2 bottom-2">
        <AutoSaveStatusIcon state={state} className="h-3.5 w-3.5" />
      </div>
    </div>
  )
}

export { AutoSaveTextarea }
export type { AutoSaveTextareaProps }
